var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import classNames from 'classnames';
import convertToTooltipProps from '../_util/convertToTooltipProps';
import Col from '../grid/col';
import { useLocale } from '../locale';
import defaultLocale from '../locale/en_US';
import Tooltip from '../tooltip';
import { FormContext } from './context';
const FormItemLabel = ({ prefixCls, label, htmlFor, labelCol, labelAlign, colon, required, requiredMark, tooltip, vertical, }) => {
    var _a;
    const [formLocale] = useLocale('Form');
    const { labelAlign: contextLabelAlign, labelCol: contextLabelCol, labelWrap, colon: contextColon, } = React.useContext(FormContext);
    if (!label) {
        return null;
    }
    const mergedLabelCol = labelCol || contextLabelCol || {};
    const mergedLabelAlign = labelAlign || contextLabelAlign;
    const labelClsBasic = `${prefixCls}-item-label`;
    const labelColClassName = classNames(labelClsBasic, mergedLabelAlign === 'left' && `${labelClsBasic}-left`, mergedLabelCol.className, {
        [`${labelClsBasic}-wrap`]: !!labelWrap,
    });
    let labelChildren = label;
    // Keep label is original where there should have no colon
    const computedColon = colon === true || (contextColon !== false && colon !== false);
    const haveColon = computedColon && !vertical;
    // Remove duplicated user input colon
    if (haveColon && typeof label === 'string' && label.trim()) {
        labelChildren = label.replace(/[:|：]\s*$/, '');
    }
    // Tooltip
    const tooltipProps = convertToTooltipProps(tooltip);
    if (tooltipProps) {
        const { icon = React.createElement(QuestionCircleOutlined, null) } = tooltipProps, restTooltipProps = __rest(tooltipProps, ["icon"]);
        const tooltipNode = (React.createElement(Tooltip, Object.assign({}, restTooltipProps), React.cloneElement(icon, {
            className: `${prefixCls}-item-tooltip`,
            title: '',
            onClick: (e) => {
                // Prevent label behavior in tooltip icon
                // https://github.com/ant-design/ant-design/issues/46154
                e.preventDefault();
            },
            tabIndex: null,
        })));
        labelChildren = (React.createElement(React.Fragment, null,
            labelChildren,
            tooltipNode));
    }
    // Required Mark
    const isOptionalMark = requiredMark === 'optional';
    const isRenderMark = typeof requiredMark === 'function';
    const hideRequiredMark = requiredMark === false;
    if (isRenderMark) {
        labelChildren = requiredMark(labelChildren, { required: !!required });
    }
    else if (isOptionalMark && !required) {
        labelChildren = (React.createElement(React.Fragment, null,
            labelChildren,
            React.createElement("span", { className: `${prefixCls}-item-optional`, title: "" }, (formLocale === null || formLocale === void 0 ? void 0 : formLocale.optional) || ((_a = defaultLocale.Form) === null || _a === void 0 ? void 0 : _a.optional))));
    }
    // https://github.com/ant-design/ant-design/pull/52950#discussion_r1980880316
    let markType;
    if (hideRequiredMark) {
        markType = 'hidden';
    }
    else if (isOptionalMark || isRenderMark) {
        markType = 'optional';
    }
    const labelClassName = classNames({
        [`${prefixCls}-item-required`]: required,
        [`${prefixCls}-item-required-mark-${markType}`]: markType,
        [`${prefixCls}-item-no-colon`]: !computedColon,
    });
    return (React.createElement(Col, Object.assign({}, mergedLabelCol, { className: labelColClassName }),
        React.createElement("label", { htmlFor: htmlFor, className: labelClassName, title: typeof label === 'string' ? label : '' }, labelChildren)));
};
export default FormItemLabel;
